import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["lightbox", "lightboxImg", "lightboxCaption", "lightboxCounter", "lightboxDownload", "shareTwitter", "shareFacebook", "shareWhatsapp", "shareMessenger", "linkButton", "prevPreview", "nextPreview"]

  connect() {
    console.log("Gallery controller connecting...")
    this.images = Array.from(this.element.querySelectorAll(".noticia-gallery-item, .gallery-grid-item")).map((el) => {
      const img = el.querySelector("img")
      const src = img?.src || ""
      const alt = img?.alt || ""
      const caption = el.dataset.caption || ""
      console.log("Found image:", { src, alt, caption })
      return { src, alt, caption }
    })
    this.currentIndex = 0
    this.handleKeyDown = (event) => this.onKeyDown(event)
    document.addEventListener("keydown", this.handleKeyDown)
    
    console.log("Gallery controller initialized with", this.images.length, "images:", this.images)
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeyDown)
    document.body.style.overflow = ""
    document.body.classList.remove("gallery-lightbox-open")
  }

  openLightbox(event) {
    console.log("openLightbox called", event)
    const index = Number(event.currentTarget.dataset.index || 0)
    console.log("Opening lightbox with index:", index, "out of", this.images.length, "images")
    this.currentIndex = index
    this.showLightbox()
  }

  closeLightbox() {
    if (!this.hasLightboxTarget) return
    this.lightboxTarget.classList.remove("is-open")
    setTimeout(() => { this.lightboxTarget.hidden = true }, 250)
    document.body.style.overflow = ""
    document.body.classList.remove("gallery-lightbox-open")
  }

  closeLightboxOnBackdrop(event) {
    if (event.target === this.lightboxTarget) this.closeLightbox()
  }

  prevImage() {
    this.slideDirection = "prev"
    this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length
    this.renderLightboxImage()
  }

  nextImage() {
    this.slideDirection = "next"
    this.currentIndex = (this.currentIndex + 1) % this.images.length
    this.renderLightboxImage()
  }

  showLightbox() {
    console.log("showLightbox called, hasLightboxTarget:", this.hasLightboxTarget)
    if (!this.hasLightboxTarget) {
      console.error("No lightbox target found")
      return
    }
    
    console.log("About to renderLightboxImage")
    this.renderLightboxImage()
    console.log("After renderLightboxImage")
    
    console.log("Removing hidden attribute, current state:", this.lightboxTarget.hasAttribute('hidden'))
    this.lightboxTarget.removeAttribute('hidden')
    console.log("After removeAttribute, hidden state:", this.lightboxTarget.hasAttribute('hidden'))
    
    console.log("Lightbox element display:", window.getComputedStyle(this.lightboxTarget).display)
    
    requestAnimationFrame(() => {
      console.log("Adding is-open class")
      this.lightboxTarget.classList.add("is-open")
      console.log("Classes on lightbox:", this.lightboxTarget.className)
      console.log("is-open class added, opacity:", window.getComputedStyle(this.lightboxTarget).opacity)
    })
    
    document.body.style.overflow = "hidden"
    document.body.classList.add("gallery-lightbox-open")
  }

  renderLightboxImage() {
    console.log("renderLightboxImage called, currentIndex:", this.currentIndex)
    const image = this.images[this.currentIndex]
    console.log("Current image:", image)
    if (!image) {
      console.error("No image found at index", this.currentIndex)
      return
    }

    // Set image
    if (this.hasLightboxImgTarget) {
      console.log("Setting image src to:", image.src)
      this.swapLightboxImage(image)
      this.updateSidePreviews()
    } else {
      console.error("lightboxImg target not found")
    }

    // Set caption
    if (this.hasLightboxCaptionTarget) {
      const caption = (image.caption || "").trim()
      const truncated = caption.length > 200 ? `${caption.slice(0, 197)}...` : caption
      console.log("Setting caption:", truncated)
      this.lightboxCaptionTarget.textContent = truncated
      this.lightboxCaptionTarget.hidden = truncated.length === 0
    } else {
      console.warn("lightboxCaption target not found")
    }

    // Set counter
    if (this.hasLightboxCounterTarget) {
      const counterHtml = `<span class="lightbox-counter-current">${this.currentIndex + 1}</span><span class="lightbox-counter-total"> de ${this.images.length}</span>`
      console.log("Setting counter:", counterHtml)
      this.lightboxCounterTarget.innerHTML = counterHtml
    } else {
      console.warn("lightboxCounter target not found")
    }

    // Set download link
    if (this.hasLightboxDownloadTarget) {
      console.log("Setting download link")
      this.lightboxDownloadTarget.href = image.src
      this.lightboxDownloadTarget.download = image.alt || `imagen-${this.currentIndex + 1}`
    } else {
      console.warn("lightboxDownload target not found")
    }

    // Set share links
    const pageUrl = this.currentShareUrl()
    const title = encodeURIComponent(`Mira esta galería de Almagro (${this.currentIndex + 1} de ${this.images.length})`)
    const encodedPage = encodeURIComponent(pageUrl)
    
    if (this.hasShareTwitterTarget) this.shareTwitterTarget.href = `https://twitter.com/intent/tweet?text=${title}&url=${encodedPage}`
    if (this.hasShareFacebookTarget) this.shareFacebookTarget.href = `https://www.facebook.com/sharer/sharer.php?u=${encodedPage}`
    if (this.hasShareWhatsappTarget) this.shareWhatsappTarget.href = `https://wa.me/?text=${title}%20${encodedPage}`
    if (this.hasShareMessengerTarget) this.shareMessengerTarget.href = `https://www.facebook.com/dialog/send?link=${encodedPage}&app_id=123456&redirect_uri=${encodedPage}`
  }

  updateSidePreviews() {
    if (!this.images.length) return

    const hasMultipleImages = this.images.length > 1
    const prevIndex = (this.currentIndex - 1 + this.images.length) % this.images.length
    const nextIndex = (this.currentIndex + 1) % this.images.length
    const prevImage = this.images[prevIndex]
    const nextImage = this.images[nextIndex]

    if (this.hasPrevPreviewTarget) {
      const prevButton = this.prevPreviewTarget.closest(".lightbox-side-preview")
      if (prevButton) prevButton.hidden = !hasMultipleImages

      if (hasMultipleImages && prevImage) {
        this.prevPreviewTarget.src = prevImage.src
        this.prevPreviewTarget.alt = prevImage.alt || "Imagen anterior"
      }
    }

    if (this.hasNextPreviewTarget) {
      const nextButton = this.nextPreviewTarget.closest(".lightbox-side-preview")
      if (nextButton) nextButton.hidden = !hasMultipleImages

      if (hasMultipleImages && nextImage) {
        this.nextPreviewTarget.src = nextImage.src
        this.nextPreviewTarget.alt = nextImage.alt || "Imagen siguiente"
      }
    }
  }

  goToImage(event) {
    this.currentIndex = Number(event.currentTarget.dataset.index || 0)
    this.renderLightboxImage()
  }

  swapLightboxImage(image) {
    console.log("swapLightboxImage called with:", image)
    const img = this.lightboxImgTarget
    console.log("lightboxImgTarget exists:", !!img)
    console.log("lightboxImgTarget element:", img)
    console.log("Setting image directly:")
    img.src = image.src
    img.alt = image.alt
    console.log("Image src set to:", img.src)
    console.log("Image element after update:", img)
    console.log("Image complete:", img.complete)
    console.log("Image naturalWidth:", img.naturalWidth)
    
    // Force browser to fetch the image if needed
    if (!img.complete && image.src) {
      img.onload = () => console.log("Image loaded successfully")
      img.onerror = () => console.error("Failed to load image:", image.src)
    }
  }

  async copyLink() {
    const url = this.currentShareUrl()

    try {
      await navigator.clipboard.writeText(url)
    } catch {
      const input = document.createElement("input")
      input.value = url
      document.body.appendChild(input)
      input.select()
      document.execCommand("copy")
      document.body.removeChild(input)
    }

    if (this.hasLinkButtonTarget) {
      const originalText = this.linkButtonTarget.textContent
      this.linkButtonTarget.textContent = "Enlace copiado"
      clearTimeout(this.copyTimeout)
      this.copyTimeout = setTimeout(() => {
        this.linkButtonTarget.textContent = originalText
      }, 1200)
    }
  }

  currentShareUrl() {
    const url = new URL(window.location.href)
    url.hash = `imagen-${this.currentIndex + 1}`
    return url.toString()
  }

  onKeyDown(event) {
    if (!this.hasLightboxTarget || this.lightboxTarget.hidden) return
    if (event.key === "Escape") this.closeLightbox()
    if (event.key === "ArrowLeft") this.prevImage()
    if (event.key === "ArrowRight") this.nextImage()
  }
}
